<template>
    <Spinner v-if="displaySpinner" />
    <Page>
        <template #title>
            <Title :level="1" class="page-title">
                <i class="pi pi-window-minimize" />
                {{ flowName }}
            </Title>
            <Tag
                icon="pi pi-exclamation-triangle"
                severity="warn"
                value="Archived"
                v-if="isArchived"
            ></Tag>
        </template>
        <template #controls>
            <LastChangedInfo
                v-if="flow?.identity"
                :entityId="flow.identity"
                :entityType="EntityType.FLOW"
            />
            <template v-if="isArchived">
                <Button
                    class="p-button-secondary"
                    severity="danger"
                    icon="pi pi-trash"
                    label="Delete"
                    @click="handleDeleteArchive"
                />
            </template>
            <template v-else>
                <Button
                    v-if="description && !description.content && isEditing"
                    v-tooltip.bottom="{
                        value: 'Will generate a first draft of the description based on the context.',
                        showDelay: 100,
                    }"
                    severity="secondary"
                    icon="pi pi-sparkles"
                    :label="
                        description?.content
                            ? 'Regenerate'
                            : 'Generate first draft'
                    "
                    @click="generateFlowDescription"
                />
            </template>
        </template>
        <Section editable>
            <TextAppArtefact
                v-if="flow?.identity && description"
                v-model="description"
                :entityId="flow.identity"
                :entityType="EntityType.FLOW"
                :isEditing="isEditing"
            />
        </Section>
        <Section v-if="flow?.entity_refs?.length || isEditing" editable>
            <template #title>Linked Domain Objects</template>
            <BusinessEntityRefs
                v-if="flow?.identity"
                :elementId="flow.identity"
                :entityRefs="flow.entity_refs ?? []"
            />
        </Section>
    </Page>
</template>

<script setup lang="ts">
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { computed, ref, watch } from "vue";

import BusinessEntityRefs from "@/components/BusinessEntityRefs.vue";
import LastChangedInfo from "@/components/LastChangedInfo.vue";
import Page from "@/components/Page.vue";
import Section from "@/components/Section.vue";
import TextAppArtefact from "@/components/TextAppArtefact.vue";
import Title from "@/components/Title.vue";
import { useEditManager } from "@/composables/edit-manager";
import { useGenerateDescriptionDialog } from "@/composables/generate-description";
import { baseSuccessToastMessage } from "@/primevue/init";
import Spinner from "@/shared/Spinner.vue";
import { EntityType } from "@/stores/entity-store";
import { useFlowManager, useFlowStore } from "@/stores/flow-store";

const toast = useToast();
const { deleteFlow } = useFlowStore();
const { isEditing } = useEditManager();
const { isFetching, description, flow } = useFlowManager();
const { generateContent } = useGenerateDescriptionDialog();

const confirm = useConfirm();

const displaySpinner = ref(false);

const isArchived = computed(() => {
    return flow.value?.is_archived;
});

const handleDeleteArchive = async (event: Event) => {
    if (!isArchived.value) {
        toast.add({
            severity: "error",
            summary: "Unable to delete flow. Flow is not archived.",
        });
        return;
    }

    confirm.require({
        target: event.currentTarget as HTMLElement,
        message: "Are you sure you want to permanently delete this flow?",
        icon: "pi pi-exclamation-triangle",
        rejectProps: {
            label: "Cancel",
            severity: "secondary",
            outlined: true,
        },
        acceptProps: {
            label: "Delete",
            severity: "danger",
        },
        accept: async () => {
            try {
                displaySpinner.value = true;
                await deleteFlow();
                toast.add({
                    ...baseSuccessToastMessage,
                    summary: "Flow has been permanently deleted",
                });
            } catch {
                toast.add({
                    severity: "error",
                    summary: "Could not delete flow",
                });
            } finally {
                displaySpinner.value = false;
            }
        },
    });
};

const flowName = computed(() => {
    if (!flow.value) {
        return "...";
    }

    return flow.value.name || flow.value.bpmn_id;
});

const generateFlowDescription = async () => {
    if (!flow.value) {
        return;
    }
    await generateContent(
        flow.value.identity,
        EntityType.FLOW,
        description.value?.updateContent.bind(description.value)
    );
};

watch(isFetching, (newValue) => (displaySpinner.value = newValue), {
    immediate: true,
});
</script>

<style scoped lang="scss">
.page-title {
    display: flex;
    align-items: center;
    gap: 6px;

    i {
        font-size: 1.6rem;
    }

    .p-tag {
        margin-left: 0.5rem;
    }
}
</style>
